// Libraries
import { mergeRight } from 'ramda';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

// MUI
import {
    GridRenderCellParams,
    GridRowParams,
    GridValidRowModel,
    useGridApiRef,
} from '@mui/x-data-grid-pro';

// Core
import { dayFormat } from '../../../core/constants';
import { ParameterValues, ResourceTextApplication } from '../../../core/resources';

// Common
import { ADeleteIconButton } from '../../../common/buttons';
import { ADataGrid, ADataGridActions, AGridColumns, gridColumns } from '../../../common/dataGrid';
import { TextWithTooltip } from '../../../common/typography';

// Interfaces
import { AnnouncementTypeEnum, DialogModeEnum } from '../../../interfaces/enums';
import { IFarmAnimal } from '../../../interfaces/IAnimal';
import {
    IAnimalAnnouncement,
    IAnimalAnnouncementAnimal,
    IAnimalAnnouncementBatch,
} from '../../../interfaces/IAnnouncement';
import { IFarmDetails } from '../../../interfaces/IBusinessEntities';

// Store
// API
// Feature
import { AnimalAnnouncementCollectWeekField } from '../../announcements/components/data-grid/AnimalAnnouncementCollectWeekField';
import ModifyCollectWeekDialog from '../../announcements/components/general/ModifyCollectWeekDialog';
import ModifySalesTypeDialog from '../../announcements/components/sales-type/ModifySalesTypeDialog';
import {
    adminCanUpdateAnimalAnnouncement,
    birthDateComparator,
    isAnimalAnnouncementBatchRowSelectable,
    isFarmDetailsValidForSalesTypeUpdate,
} from '../../announcements/helpers/data';
import ConfirmDialog from '../../shared/components/confirm/ConfirmDialog';
import { Strings } from '../../shared/constants/strings';
import { IModifyCollectWeekDialogProps, ISalesTypeDialogProps } from '../../shared/types/propTypes';
import moment from 'moment';
import { hasOnlyExpressCow } from '../../announcements/helpers/animal';
import { IProducerReturn } from '../../../interfaces/IProducerReturn';

export interface IManualAnnouncementAnimalsGridProps {
    announcementData: IAnimalAnnouncement | null;
    announcementAnimals: IAnimalAnnouncementAnimal[];
    selectedAnimals: IAnimalAnnouncementAnimal[];
    setSelectedAnimals: (animals: IAnimalAnnouncementAnimal[]) => void;
    allBatches: IAnimalAnnouncementBatch[];
    announcementType: AnnouncementTypeEnum;
    bovineSexParams: ParameterValues;
    dialogMode: DialogModeEnum;
    farmDetails: IFarmDetails | undefined;
    isLoading: boolean;
    isModifyCollectWeekDialogVisible: boolean;
    isReadOnlyMode?: boolean;
    isSalesTypeDialogVisiable: boolean;
    onAnimalAnimalSalesTypeUpdate: (animal: IFarmAnimal[]) => void;
    onAnimalRemoveFromGrid: (animal: IAnimalAnnouncementAnimal) => void;
    onCloseModifyCollectWeekDialog: (cancelled: boolean | undefined) => void;
    onCloseSalesTypeDialog: () => void;
    salesTypeParams: ParameterValues;
    mode: DialogModeEnum;
    producerReturn: IProducerReturn | null;
}

export const ManualAnnouncementAnimalsGrid = (props: IManualAnnouncementAnimalsGridProps) => {
    const { selectedAnimals, setSelectedAnimals } = props;
    const { t } = useTranslation<ResourceTextApplication[]>(['AnelmaBovine']);

    const [confirmDialogVisible, setConfirmDialogVisible] = useState<boolean>(false);
    const [selectedAnimal, setSelectedAnimal] = useState<IAnimalAnnouncementAnimal | null>(null);
    const [rows, setRows] = useState<GridValidRowModel[]>([]);

    useEffect(() => {
        props.announcementAnimals.length > 0
            ? setRows(
                props.announcementAnimals.map(
                    (a) => {
                        // TODO: Refactor to use batches as rows
                        // to work the same way as normal announcements
                        let batchIndex = props.allBatches.findIndex(b => b.Animals[0].AnimalId === a.AnimalId);

                        if (batchIndex != -1)
                            return mergeRight(a, { id: a.AnimalId, BatchStatus: props.allBatches[batchIndex].BatchStatus });

                        return mergeRight(a, { id: a.AnimalId });
                    }))
            : setRows([]);
    }, [props.announcementAnimals]);

    const columns: AGridColumns = [
        gridColumns.euIdentifier(t('AnelmaBovine:1005')),
        {
            field: 'BirthDate',
            headerName: t('AnelmaBovine:1011'),
            renderCell: (params) => {
                if (!params.value || params.value === '0001-01-01') return '';

                const birthDate = String(params.value as Date);

                return birthDate ? (
                    <TextWithTooltip text={`${moment(birthDate).format(dayFormat)}`} />
                ) : (
                    ''
                );
            },
            width: 150,
            type: 'date',
            valueGetter: (params) => {
                if (isNaN(Date.parse(params)))
                    return '';
                return new Date(params);
            },
        },
        {
            field: 'Sex',
            headerName: t('AnelmaBovine:1048'),
            renderCell: (param) => {
                const value = (param.row as IAnimalAnnouncementAnimal)?.Sex;

                const bovineSexText =
                    props.bovineSexParams.find((p) => Number(p.code) === value)?.text ?? '';
                return <TextWithTooltip text={bovineSexText} />;
            },
        },
        {
            field: 'SalesType',
            headerName: t('AnelmaBovine:1019'),
            renderCell: (param) => {
                const value = (param.row as IAnimalAnnouncementAnimal)?.SalesType;

                const salesTypeText =
                    props.salesTypeParams.find((p) => Number(p.code) === value)?.text ?? '';

                return <TextWithTooltip text={salesTypeText} />;
            },
            width: 150,
        },
        {
            field: 'Week',
            headerName: t('AnelmaBovine:1097'),
            renderCell: (params) => {
                const animalRow = params.row as IAnimalAnnouncementAnimal;
                const batch = getBatchByAnimalId(animalRow?.AnimalId);

                if (!batch && animalRow.CollectWeekStart && animalRow.CollectWeekEnd) {
                    return (
                        <AnimalAnnouncementCollectWeekField
                            collectWeekStart={animalRow.CollectWeekStart}
                            collectWeekEnd={animalRow.CollectWeekEnd}
                            mode={props.mode}
                        />
                    );
                }
                if (!batch) return '';
                return (
                    <AnimalAnnouncementCollectWeekField
                        collectWeekStart={batch.CollectWeekStart}
                        collectWeekEnd={batch.CollectWeekEnd}
                        mode={props.mode}
                    />
                );
            },
            width: 150,
        },
        {
            field: 'BatchInfo',
            headerName: t('AnelmaBovine:1039'),
            renderCell: (param) => {
                return <TextWithTooltip text={(param?.value as string) ?? ''} />;
            },
            valueGetter: (param, row) => {
                const batch = getBatchByAnimalId((row as IAnimalAnnouncementAnimal)?.AnimalId);
                return batch ? batch.BatchInfo : '';
            },
        },
    ];

    const isDeleteDisabled = (params: GridRenderCellParams) => {
        const adminCanUpdateAnnouncement = adminCanUpdateAnimalAnnouncement(
            props.announcementData,
            props.announcementType
        );

        if (props.isLoading)
            return true;

        // remove producer return first
        let euIdentifier = '';
        if (params.row.Animals && (params.row.Animals as IAnimalAnnouncementAnimal[]).length === 1)
            euIdentifier = (params.row.Animals as IAnimalAnnouncementAnimal[])[0].EuIdentifier;
        else if (params.row.EuIdentifier)
            euIdentifier = params.row.EuIdentifier;

        if (props.producerReturn?.EUIdentifier === euIdentifier)
            return true;

        if (props.isReadOnlyMode && props.isReadOnlyMode === true)
            return true;

        let collectWeek = props.allBatches.find((a) =>
            a.Animals.find((b) => b.AnimalId === params.row.AnimalId)
        )?.CollectWeekStart;

        if (props.mode === 'edit' && collectWeek)
            return !isAnimalAnnouncementBatchRowSelectable(props.announcementType, collectWeek, adminCanUpdateAnnouncement, params.row.BatchStatus);
    };

    const getBatchByAnimalId = (animalId: string): IAnimalAnnouncementBatch | null => {
        return props.allBatches.find((b) => b.Animals[0]?.AnimalId === animalId) ?? null;
    };

    const getActionTooltipText = (): string => {
        if (props.announcementType === AnnouncementTypeEnum.ManualBovineSlaughter)
            return t('AnelmaBovine:1045');
        else if (props.announcementType === AnnouncementTypeEnum.ManualBovineIntermediation)
            return t('AnelmaBovine:1151');
        else return '';
    };

    const actions: ADataGridActions = [];

    actions.push((params) => {
        return (
            <ADeleteIconButton
                key={`${params.id}-delete`}
                onClick={() => {
                    setConfirmDialogVisible(true);
                    const animal = params.row as unknown as IAnimalAnnouncementAnimal;
                    if (animal) {
                        setSelectedAnimal(animal);
                    }
                }}
                tooltip={getActionTooltipText()}
                disabled={isDeleteDisabled(params)}
            />
        );
    });

    const deleteAnimalFromAnnouncement = () => {
        if (selectedAnimal) {
            props.onAnimalRemoveFromGrid(selectedAnimal);
            setSelectedAnimal(null);
            setSelectedAnimals([]);
        }
    };

    const isBatchSelectable = (params: GridRowParams): boolean => {
        const adminCanUpdateAnnouncement = adminCanUpdateAnimalAnnouncement(
            props.announcementData,
            props.announcementType
        );

        if (props.isReadOnlyMode && props.isReadOnlyMode === true)
            return false;

        if (selectedAnimal?.AnimalId === params.row?.AnimalId
            && !props.announcementAnimals.find((i) => i.AnimalId === selectedAnimal?.AnimalId)) {
            return false;
        }

        const batch = getBatchByAnimalId(params.row?.AnimalId);

        return batch
            ? props.dialogMode === 'edit'
                ? isAnimalAnnouncementBatchRowSelectable(
                    props.announcementType,
                    batch.CollectWeekStart,
                    adminCanUpdateAnnouncement,
                    params.row.BatchStatus
                )
                : true
            : true;
    };

    const salesTypeDialogProps: ISalesTypeDialogProps = {
        id: props.announcementData?.Id ?? '',
        onClose: () => props.onCloseSalesTypeDialog(),
        animals: selectedAnimals,
        allAnimals: null,
        handleSalesTypeUpdate: (data: IFarmAnimal[]) => {
            props.onAnimalAnimalSalesTypeUpdate(data);
        },
        farmId: isFarmDetailsValidForSalesTypeUpdate(props.farmDetails),
        farmDetails: props.farmDetails,
        announcementType: props.announcementType,
        mode: props.dialogMode,
    };

    const modifyCollectWeekDialogProps: IModifyCollectWeekDialogProps = {
        onClose: (cancelled: boolean | undefined) => {
            props.onCloseModifyCollectWeekDialog(cancelled);
        },
        announcementType: props.announcementType,
        animals: selectedAnimals,
        batches: props.allBatches,
    };

    return (
        <>
            <ADataGrid
                disableSelectionOnClick={true}
                actions={actions}
                columns={columns}
                enableFiltering
                loading={props.isLoading}
                onSelectionModelChange={(selectionModel) => {
                    setSelectedAnimals(props.announcementAnimals.filter(a => selectionModel.includes(a.AnimalId)));
                }}
                rows={rows}
                isRowSelectable={(params) => isBatchSelectable(params)}
            />

            {confirmDialogVisible && (
                <ConfirmDialog
                    title={'AnelmaGeneral:1142'}
                    content={'AnelmaGeneral:1012'}
                    actionButtonType={Strings.AnnouncementDeleteConfirmActionType}
                    onClose={() => setConfirmDialogVisible(false)}
                    onConfirm={() => deleteAnimalFromAnnouncement()}
                />
            )}

            {props.isSalesTypeDialogVisiable && <ModifySalesTypeDialog {...salesTypeDialogProps} />}
            {props.isModifyCollectWeekDialogVisible && (
                <ModifyCollectWeekDialog {...modifyCollectWeekDialogProps} />
            )}
        </>
    );
};
